// Reserved for custom JavaScript

/* ── Locale-aware money formatting ────────────────────────────────────────
   shop.money_format is a single global template (e.g. "£{{amount}}") shared
   across all markets. Shopify Markets swaps the symbol when the active
   currency differs from the shop's base currency, but the symbol POSITION
   stays the same — so on the French / German markets we get "€99,99"
   instead of the local conventions ("99,99 €" / "99,99 €"). This module
   reformats prices client-side via Intl.NumberFormat per active locale.

   Per-locale Intl output for EUR:
     fr-FR → "1 234,56 €"   (NBSP thousands, NBSP before symbol)
     de-DE → "1.234,56 €"   (period thousands, regular space before symbol)
     en-IE → "€1,234.56"    (symbol prefix — Irish English keeps UK layout)

   FR / DE are keyed on the request language. Ireland shares `en` with the
   UK market (which must keep the default Shopify output untouched), so it
   is keyed on the market country (window.tntCountry) instead — running
   every Euro market through the same Intl pipeline keeps Liquid, Rebuy and
   custom-JS price surfaces consistent per market.

   - window.tntFormatMoney(majorAmount, currency) — explicit formatter for
     custom JS. Returns an Intl-formatted string when the active locale is
     in LOCALE_MAP, null otherwise (caller falls back to its existing
     Shopify-format output).
   - DOM post-processor — on supported locales only, scans .money /
     .rebuy-money / .modular-hero__price-amount elements and reformats their
     text. Covers Liquid `| money` outputs and Rebuy formatMoney outputs
     without needing to rewrite ~70 callsites. A MutationObserver picks up
     dynamic prices (variant changes, Rebuy widget re-renders, lazy-loaded
     sections).
   ──────────────────────────────────────────────────────────────────────── */

(function initLocaleMoney() {
  const locale = window.tntLocale || document.documentElement.lang || 'en';
  const defaultCurrency = window.tntCurrency || 'GBP';
  const country = (window.tntCountry || '').toUpperCase();

  // Map the active language to the BCP-47 tag used for Intl.NumberFormat.
  // Add new entries here to enable client-side reformatting for additional
  // markets — Intl decides the thousands separator and symbol position.
  const LOCALE_MAP = {
    fr: 'fr-FR',
    de: 'de-DE',
  };

  // Country-keyed fallback for markets whose language doesn't identify a
  // single formatting convention: `en` is shared by the UK (default Shopify
  // output, no reformatting) and Ireland (Intl-formatted like the other
  // Euro markets). A specific language above still wins — a French-language
  // session formats fr-FR whichever market it's browsing.
  const COUNTRY_LOCALE_MAP = {
    IE: 'en-IE',
  };

  const lang = locale.toLowerCase().split('-')[0];
  const intlLocale = LOCALE_MAP[lang] || COUNTRY_LOCALE_MAP[country] || null;
  const shouldReformat = Boolean(intlLocale);

  const SYMBOL_TO_ISO = { '£': 'GBP', '€': 'EUR', '$': 'USD' };

  function formatIntl(amount, currency) {
    try {
      return new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency: currency || defaultCurrency,
      }).format(amount);
    } catch {
      return null;
    }
  }

  // Major-unit (decimal) formatter used by other custom JS. Returns null on
  // unsupported locales so callers preserve their existing Shopify-format
  // output.
  window.tntFormatMoney = function (majorAmount, currency) {
    if (!shouldReformat) return null;
    const n = Number(majorAmount);
    if (!Number.isFinite(n)) return null;
    return formatIntl(n, currency);
  };

  if (!shouldReformat) return;

  // Parse a Shopify-rendered price into a major-unit number, handling both
  // decimal conventions:
  //   £1,234.56 / £99.99   → dot is decimal separator
  //   €1.234,56 / €99,99   → comma is decimal separator
  // The LAST separator in the string is treated as the decimal point —
  // UNLESS exactly 3 digits follow it ("€1,234" / "€1.234"), which GBP/EUR
  // amounts can only get from a thousands separator with the decimals
  // stripped (money_without_trailing_zeros, e.g. roundel discounts). Money
  // never has 3 decimal places on this store, so treat that as thousands.
  // ALL separators before the decimal point are stripped regardless of kind,
  // so malformed mixed-separator strings ("€6.999.00" — the Irish store's
  // Rebuy formatMoney rendering of a fractional-cents float) still parse to
  // the intended 6999.00 instead of parseFloat stopping at the second dot.
  function parseAmount(text) {
    const cleaned = (text || '').replace(/[^\d,.\-]/g, '');
    if (!cleaned || !/\d/.test(cleaned)) return NaN;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const lastSep = Math.max(lastComma, lastDot);
    let num;
    if (lastSep === -1) {
      num = parseFloat(cleaned);
    } else if (cleaned.length - lastSep - 1 === 3) {
      num = parseFloat(cleaned.replace(/[,.]/g, ''));
    } else {
      num = parseFloat(
        cleaned.slice(0, lastSep).replace(/[,.]/g, '') + '.' + cleaned.slice(lastSep + 1)
      );
    }
    return Number.isFinite(num) ? num : NaN;
  }

  function detectCurrency(text) {
    for (const [sym, iso] of Object.entries(SYMBOL_TO_ISO)) {
      if (text.includes(sym)) return iso;
    }
    return defaultCurrency;
  }

  // Match money substrings inside arbitrary text. Captures three forms:
  //   €99,99 / £1,234.56  (symbol-prefix)
  //   99,99 €             (symbol-suffix, already FR-style)
  // Used inside .replace() with the global flag so price ranges like
  // "From €10 to €20" or "€10 – €20" reformat both prices, not just the
  // first. Loose anchoring also handles prefixed strings like "Save €10".
  const MONEY_RE = /([£€$]\s*\d[\d,.\s]*\d|[£€$]\s*\d|\d[\d,.\s]*\d\s*[£€$]|\d\s*[£€$])/g;

  // Track which elements we've already formatted (to skip re-work) and
  // which ones we're CURRENTLY writing to (so the resulting characterData
  // mutation isn't treated as an external update — would cause an infinite
  // reformat loop).
  const formattedEls = new WeakSet();
  const writingEls = new WeakSet();

  // Selectors for elements whose text content is (or contains) a money
  // value. Keep this list narrow — we don't want to reformat random spans.
  const SELECTOR =
    '.money, .rebuy-money, .modular-hero__price-amount, .modular-bundle__progress-tier-value';

  function reformatElement(el) {
    if (formattedEls.has(el)) return;
    // Skip wrappers that only contain other money elements — let the leaves
    // handle their own text. Without this, parent text is the concatenated
    // children's text and we'd double-format.
    if (el.querySelector(SELECTOR)) return;

    const text = el.textContent || '';
    if (!text.trim()) return;

    // Walk descendant text nodes and modify only the ones containing money
    // values. Earlier versions assigned el.textContent = newText, which
    // collapses sibling element structure — e.g. <span class="sr-only">
    // labels for screen readers — into plain text, leaking the label into
    // the visible UI. By writing to individual text nodes we preserve every
    // sibling element exactly as Vue rendered it.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const writes = [];
    let node;
    while ((node = walker.nextNode())) {
      const raw = node.nodeValue || '';
      let nodeChanged = false;
      const replaced = raw.replace(MONEY_RE, (match) => {
        const amount = parseAmount(match);
        if (!Number.isFinite(amount)) return match;
        const ccy = detectCurrency(match);
        const formatted = formatIntl(amount, ccy);
        if (!formatted || formatted === match) return match;
        nodeChanged = true;
        return formatted;
      });
      if (nodeChanged) writes.push({ node, value: replaced });
    }

    if (!writes.length) {
      formattedEls.add(el);
      return;
    }

    writingEls.add(el);
    for (const { node: textNode, value } of writes) {
      textNode.nodeValue = value;
    }
    formattedEls.add(el);
  }

  function reformatAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(reformatElement);
  }

  function init() {
    reformatAll();

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        // characterData: text node inside a money element was edited.
        if (m.type === 'characterData') {
          const parent = m.target.parentElement?.closest(SELECTOR);
          if (!parent) continue;
          // Filter out our own writes to break the reformat → mutation →
          // reformat loop. textContent assignment usually triggers childList
          // (text node replaced) rather than characterData, but a few code
          // paths edit textNode.data directly — covered here.
          if (writingEls.has(parent)) {
            writingEls.delete(parent);
            continue;
          }
          formattedEls.delete(parent);
          reformatElement(parent);
          continue;
        }
        // childList on the money element itself: textContent / innerHTML
        // assignment by external code (variant change, Rebuy re-render)
        // replaces the children of the money element. Reprocess.
        if (m.target instanceof Element && m.target.matches(SELECTOR)) {
          if (writingEls.has(m.target)) {
            writingEls.delete(m.target);
          } else {
            formattedEls.delete(m.target);
            reformatElement(m.target);
          }
          // Fall through to also check addedNodes for nested money elements.
        }
        // childList on an ancestor: new money elements added to the DOM
        // (lazy-loaded sections, Rebuy widgets compiling, Vue re-renders).
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(SELECTOR)) reformatElement(node);
          node.querySelectorAll?.(SELECTOR).forEach(reformatElement);
        }
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── Shadow DOM style injection ───────────────────────────────────────────
   square-placement and klarna-placement are Web Components whose internals
   live inside a shadow root, so normal CSS cannot reach them.
   We inject a <style> element directly into each shadow root once the
   element is connected to the DOM.
   ──────────────────────────────────────────────────────────────────────── */

const SHADOW_STYLES = {
  'square-placement': `
    p.afterpay-paragraph {
      text-align: center;
    }
  `,
  'klarna-placement': `
    div.container.default {
      border: none;
      flex-direction: row-reverse;
      justify-content: center;
      gap: 20px;
      padding: 15px;
      border-radius: 20px;
      font-family: 'Figtree', sans-serif;
      background-color: #F7F0EA;
      color: #003d50;
    }
    div.container.default div.text-wrapper {
      margin-left: 0;
    }
    div.container.default div.text-wrapper p.osm-message {
      font-size: 1.1em;
    }
  `,
};

function injectShadowStyles(element, css) {
  // Wait until the element has a shadow root (some components attach it lazily)
  if (!element.shadowRoot) return;
  // Avoid double-injecting
  if (element.shadowRoot.querySelector('style[data-tnt]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-tnt', '');
  style.textContent = css;
  element.shadowRoot.appendChild(style);
}

function observeAndInject(tagName, css) {
  // Inject into any instances already in the DOM
  document.querySelectorAll(tagName).forEach((el) => injectShadowStyles(el, css));

  // Watch for new instances being added
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(tagName)) injectShadowStyles(node, css);
        node.querySelectorAll(tagName).forEach((el) => injectShadowStyles(el, css));
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

Object.entries(SHADOW_STYLES).forEach(([tagName, css]) => {
  // Defer until the custom element is defined, then start observing
  customElements.whenDefined(tagName).then(() => observeAndInject(tagName, css));
});

/* ── Compare-at price 'Was' label visibility ──────────────────────────────
   empire.js replaces the innerHTML of [data-price-compare-container] with
   content from a hidden template element. That content can carry the
   visually-hidden class on the 'Was' label span, causing it to disappear
   after the initial render.
   We use a MutationObserver to strip visually-hidden from the label span
   whenever empire.js swaps the content. We only act on containers that
   have opted in via data-show-original-label, which product-price.liquid
   sets when hide_original_label is false.
   ──────────────────────────────────────────────────────────────────────── */

function fixCompareAtLabel(container) {
  // Target all spans that are NOT money/price spans, regardless of whether
  // they currently carry visually-hidden (empire.js may or may not include it)
  container.querySelectorAll('span').forEach((span) => {
    if (
      !span.hasAttribute('data-price-compare') &&
      !span.hasAttribute('data-price-compare-min') &&
      !span.hasAttribute('data-price-compare-max') &&
      !span.classList.contains('money')
    ) {
      span.classList.remove('visually-hidden');
      span.classList.add('price__compare-at--label');
    }
  });
}

function initComparePriceLabelObservers() {
  // data-show-original-label lives on [data-product-pricing], which empire.js
  // never replaces. We look inside it for [data-price-compare-container],
  // which empire.js does replace, and fix the label span each time.
  document.querySelectorAll('[data-show-original-label]').forEach((pricingEl) => {
    function fix() {
      const container = pricingEl.querySelector('[data-price-compare-container]');
      if (container) fixCompareAtLabel(container);
    }

    fix(); // Correct initial render

    // childList + subtree catches both innerHTML swaps on the compare-at div
    // and full element replacements by a parent-level innerHTML update
    new MutationObserver(fix).observe(pricingEl, { childList: true, subtree: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initComparePriceLabelObservers);
} else {
  initComparePriceLabelObservers();
}

/* ── Hide empty iwt_dimensions section ────────────────────────────────────
   If the ul.iwt_dimensions_bullets list contains no <li> elements the
   entire Image-with-Text dimensions section should be invisible.
   CSS :has() is unreliable here because whitespace-only text nodes affect
   how some browsers evaluate :not(:has(*)), so we use JS instead.
   A MutationObserver keeps the state correct if content is injected later
   (e.g. by a section/block re-render in the theme editor).
   ──────────────────────────────────────────────────────────────────────── */

function applyDimensionsVisibility(section) {
  // Target the outer .shopify-section wrapper so that class/style changes on
  // the inner <section> element (animations, empire.js, etc.) don't interfere.
  const wrapper = section.closest('.shopify-section') || section;

  function update() {
    const ul = section.querySelector('ul.iwt_dimensions_bullets');
    const isEmpty = !ul || ul.querySelector('li') === null;
    wrapper.style.display = isEmpty ? 'none' : '';
  }

  update();

  // Watch for content changes inside the section (e.g. variant-driven updates)
  new MutationObserver(update).observe(section, { childList: true, subtree: true });
}

function initDimensionsSectionVisibility() {
  // Handle any sections already in the DOM
  document.querySelectorAll('[data-section-name="iwt_dimensions"]').forEach(applyDimensionsVisibility);

  // Watch for sections injected later (lazy-loaded as the page scrolls)
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('[data-section-name="iwt_dimensions"]')) applyDimensionsVisibility(node);
        node.querySelectorAll('[data-section-name="iwt_dimensions"]').forEach(applyDimensionsVisibility);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDimensionsSectionVisibility);
} else {
  initDimensionsSectionVisibility();
}


/* ── Header cart count sync ────────────────────────────────────────────────
   The theme renders [data-header-cart-count] on page load, but Rebuy's Smart
   Cart updates the cart without triggering the theme's own count refresh.
   We keep it in sync by updating the attribute and toggling the visible class
   on every rebuy:cart.change.
   ──────────────────────────────────────────────────────────────────────── */

document.addEventListener('rebuy:cart.change', (event) => {
  const el = document.querySelector('[data-header-cart-count]');
  if (!el) return;
  const count = event.detail.cart.itemCount();
  el.setAttribute('data-header-cart-count', count);
  el.classList.toggle('visible', count > 0);
});

/* Rebuy Smart Cart */

// Estimate how many units of a line item actually received a given discount.
// Shopify Functions can apply a bundle discount to a subset of a line's
// quantity (e.g. "1 free per primary"); the line as a whole still reports
// the discount entry. Without this calculation we'd over-count discounted
// units and remove valid lines.
//
// Strategy: derive count from total discount allocated divided by the
// per-unit original price. This is exact for "free with purchase" rules
// (per-unit discount == price, so total / price == discounted count) and
// also produces a sensible whole-number count for percentage discounts that
// apply uniformly across the whole line. If the math doesn't yield a clean
// integer in [1, quantity] we fall back to assuming the whole line is
// discounted, which preserves the original behaviour for unfamiliar
// discount shapes.
function getDiscountedUnitCount(item, discountFilter) {
  const discount = item.discounts?.find(discountFilter);
  if (!discount) return 0;

  const total = discount.total_allocated_amount ?? discount.amount;
  const perUnit = item.original_price;
  if (typeof total !== 'number' || !perUnit) return item.quantity;

  const ratio = total / perUnit;
  const rounded = Math.round(ratio);
  if (rounded > 0 && rounded <= item.quantity && Math.abs(ratio - rounded) < 0.05) {
    return rounded;
  }
  return item.quantity;
}

function syncBundleAddons(cart) {
  // ── Footstool bundle addon sync (tag-based matching) ────────────────────
  // Products opt in by carrying one or more tags beginning with 'Footstool:'.
  // A discounted footstool unit is valid only when an unmatched partner
  // sharing at least one Footstool: tag value exists in the cart. Excess
  // discounted units are removed (line qty is trimmed; whole-line removal
  // only when ALL units on the line are unmatched).
  //
  // Tags are compared case-insensitively. item.product is Rebuy's enriched
  // product, fetched with the storefront locale (locale=en), and Rebuy's
  // per-locale product records can hold every tag lowercased
  // ("footstool:icodomvt018-icofstrndvt018") while the paired product keeps
  // its original case. A case-sensitive match misses the lowercased partner
  // and removes a valid footstool a second after it's added.
  const FOOTSTOOL_PREFIX = 'footstool:';

  const getFootstoolTags = (item) => {
    const raw = item.product?.tags ?? '';
    const tags = Array.isArray(raw) ? raw : raw.split(',');
    return tags
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t.startsWith(FOOTSTOOL_PREFIX))
      .map((t) => t.slice(FOOTSTOOL_PREFIX.length));
  };

  const isBundleDiscount = (d) => d.title?.toLowerCase().includes('bundle');

  const discountedUnits = [];
  const partnerUnits = [];

  cart.getItems().forEach((i) => {
    const tags = getFootstoolTags(i);
    if (!tags.length) return;

    const discountedQty = getDiscountedUnitCount(i, isBundleDiscount);

    if (discountedQty > 0) {
      for (let n = 0; n < discountedQty; n++) discountedUnits.push({ item: i, tags });
    } else if (i.product_type !== 'Footstools') {
      for (let n = 0; n < i.quantity; n++) partnerUnits.push({ item: i, tags, matched: false });
    }
  });

  // Greedily match each discounted unit to an unmatched partner sharing a tag.
  // Track unmatched count PER LINE so we can trim qty on partial mismatches
  // instead of deleting the whole line (the previous behaviour killed valid
  // matched units alongside the unmatched ones).
  const unmatchedCountByKey = new Map();
  const itemByKey = new Map();
  for (const du of discountedUnits) {
    const idx = partnerUnits.findIndex(
      (pu) => !pu.matched && du.tags.some((t) => pu.tags.includes(t))
    );
    if (idx !== -1) {
      partnerUnits[idx].matched = true;
    } else {
      const key = du.item.key;
      unmatchedCountByKey.set(key, (unmatchedCountByKey.get(key) ?? 0) + 1);
      itemByKey.set(key, du.item);
    }
  }

  for (const [key, unmatchedCount] of unmatchedCountByKey) {
    const item = itemByKey.get(key);
    if (!item) continue;
    if (unmatchedCount >= item.quantity) {
      cart.removeItem(item);
    } else {
      // Trim the line by the unmatched count. Use the line item key
      // (variant_id:hash) rather than variant_id — it uniquely identifies
      // this specific line, which matters when multiple lines share a
      // variant (e.g. discount-split addon lines). Rebuy's cart sync
      // picks up the change and re-fires rebuy:cart.change, but
      // syncBundleAddons is idempotent so the second pass is a no-op.
      const newQty = item.quantity - unmatchedCount;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: newQty }),
      }).catch((err) => console.error('[syncBundleAddons] trim failed:', err));
    }
  }

  // ── Set of 2 sync ────────────────────────────────────────────────────────
  const byParentSku = new Map();

  cart.getItems().forEach((i) => {
    const parentSku = i.product?.metafields?.custom?.parent_sku;
    if (!parentSku) return;

    if (!byParentSku.has(parentSku)) {
      byParentSku.set(parentSku, { fullPriceQty: 0, discounted: [] });
    }
    const group = byParentSku.get(parentSku);
    const hasSetOf2Discount = i.discounts?.some((d) => d.title === 'Set of 2');
    if (hasSetOf2Discount) {
      for (let n = 0; n < i.quantity; n++) group.discounted.push(i);
    } else {
      group.fullPriceQty += i.quantity;
    }
  });

  byParentSku.forEach((group) => {
    const excess = group.discounted.length - group.fullPriceQty;
    const seen = new Set();
    let removed = 0;
    for (let i = 0; i < group.discounted.length && removed < excess; i++) {
      if (!seen.has(group.discounted[i].key)) {
        seen.add(group.discounted[i].key);
        cart.removeItem(group.discounted[i]);
        removed++;
      }
    }
  });
}

document.addEventListener('rebuy:cart.change', (event) => {
  const { cart } = event.detail;
  cart.enrichCart(() => syncBundleAddons(cart));
});

/* ── Rebuy widget #284621 – conditional display ────────────────────────────
   The Rebuy data source rule triggers when cart item count is 0, but the
   popup fires before the cart change propagates. We switch to manual trigger
   and show it ourselves once the cart updates, gated on:
     - exactly one line item in the cart
     - that item's product has metafield custom.2_pack = true
   ──────────────────────────────────────────────────────────────────────── */

(function initConditionalUpsell() {
  document.addEventListener('rebuy.ready', (event) => {
    if (event.detail.widget?.id !== '284621') return;
    const widget = event.detail.widget;
    widget.data.pop_up_trigger = 'manual';

    // The inner .rebuy-widget-container gets is-visible added when the popup
    // actually displays — watch it and immediately hide if footstool is selected.
    const rootEl = document.getElementById('rebuy-widget-284621');
    if (!rootEl) return;

    const container = rootEl.querySelector('.rebuy-widget-container');
    if (!container) return;

    new MutationObserver(() => {
      if (!container.classList.contains('is-visible')) return;
      const addonWidget = window.Rebuy?.widgets?.find((w) => w.id === '276830');
      const footstoolSelected = addonWidget?.data?.products?.some((p) => p.selected) ?? false;
      if (footstoolSelected) widget.hide();
    }).observe(container, { attributes: true, attributeFilter: ['class'] });
  });

  document.addEventListener('rebuy:cart.change', (event) => {
    const { cart } = event.detail;
    cart.enrichCart(() => {
      const items = cart.getItems();
      if (items.length !== 1) return;
      if (!items[0].product?.metafields?.custom?.['2_pack']) return;

      const addonWidget = window.Rebuy?.widgets?.find((w) => w.id === '276830');
      if (addonWidget?.data?.products?.some((p) => p.selected)) return;

      const widget = window.Rebuy?.widgets?.find((w) => w.id === '284621');
      if (widget?.data?.products?.length) widget.show();
    });
  });
})();

/* ── Rebuy Smart Cart trapFocus error suppression ─────────────────────────
   Rebuy's SmartCartV2.js calls trapFocus() on its container before Vue has
   finished rendering the cart content. With zero focusable elements at that
   instant, theme-a11y's trapFocus crashes with:
     "Cannot read properties of undefined (reading 'focus')"
   This is a bug in Rebuy's own bundle that we cannot patch. The crash is
   benign — the cart opens and functions correctly — so we suppress it.
   ──────────────────────────────────────────────────────────────────────── */

window.addEventListener('error', (event) => {
  if (
    event.message?.includes("reading 'focus'") &&
    event.error?.stack?.includes('trapFocus')
  ) {
    event.preventDefault();
  }
});

/* ── AddToCartFlyout + Rebuy Smart Cart compatibility ─────────────────────
   The native theme cart is hidden via CSS so we use the Rebuy Smart Cart
   instead. This causes a console error when an ATC flyout closes:

   AddToCartFlyout.js: Cannot read properties of null (reading 'remove')
      _closeFlyOut() is triggered twice concurrently; the second promise
      callback calls .remove() on a flyOut already set to null.

   Fix: patch AddToCartFlyout.prototype._closeFlyOut with a guard BEFORE any
   instance is created. The constructor does:
     this._closeFlyOut = this._closeFlyOut.bind(this)
   so if the prototype method has the guard when binding happens, every
   instance's bound own-property _closeFlyOut includes the guard — regardless
   of whether the instance is created via window.AddToCartFlyout or a local
   module reference in empire.js.

   _updateCart is NOT bound in the constructor (only called via this.method()),
   so patching it on the prototype lets us track activeInstance reliably even
   when new AddToCartFlyout() bypasses our constructor wrapper.

   On rebuy:smartcart.show we call _closeFlyOut() to remove the broken
   trapFocus handler before Rebuy takes over focus management.
   ──────────────────────────────────────────────────────────────────────── */

(function patchAddToCartFlyout() {
  let activeInstance = null;

  function applyPrototypePatches(Cls) {
    if (!Cls?.prototype || Cls.prototype._tntPatched) return;
    Cls.prototype._tntPatched = true;

    // Guard against double-close race (fixes null.remove() error)
    const originalClose = Cls.prototype._closeFlyOut;
    Cls.prototype._closeFlyOut = function () {
      if (this._tntClosing) return;
      if (!this.flyOut) return;
      this._tntClosing = true;
      originalClose.call(this);
    };

    // Track instances — _updateCart is not bound so it always reads prototype
    const originalUpdateCart = Cls.prototype._updateCart;
    Cls.prototype._updateCart = function () {
      activeInstance = this;
      return originalUpdateCart.call(this);
    };
  }

  function setup(Cls) {
    applyPrototypePatches(Cls);

    // Also wrap the constructor so activeInstance is set even if _updateCart
    // is somehow called before our prototype patch takes effect
    const Wrapper = function (...args) {
      const instance = new Cls(...args);
      activeInstance = instance;
      return instance;
    };
    Object.setPrototypeOf(Wrapper, Cls);
    Wrapper.prototype = Cls.prototype;
    return Wrapper;
  }

  if (window.AddToCartFlyout) {
    window.AddToCartFlyout = setup(window.AddToCartFlyout);
  } else {
    Object.defineProperty(window, 'AddToCartFlyout', {
      configurable: true,
      set(val) {
        Object.defineProperty(window, 'AddToCartFlyout', {
          configurable: true,
          writable: true,
          value: setup(val),
        });
      },
    });
  }

  document.addEventListener('rebuy:smartcart.show', () => {
    if (!activeInstance) return;

    const deadline = Date.now() + 2000;

    function closeWhenReady() {
      if (activeInstance._tntClosing) return;
      if (activeInstance.flyOut) {
        activeInstance._closeFlyOut();
      } else if (Date.now() < deadline) {
        requestAnimationFrame(closeWhenReady);
      }
    }

    closeWhenReady();
  });
})();

/* ── Footstool bundle image swap ───────────────────────────────────────────
   When a footstool addon (widget #276830) is selected, the main product
   gallery switches to the matching bundle image (alt text "bundle:SKU").
   The gallery's goToSlide is patched to skip bundle slides during normal
   swipe/keyboard navigation; our explicit switches use a bypass flag.
   ──────────────────────────────────────────────────────────────────────── */

(function initFootstoolImageSwap() {
  function getGalleryEl() {
    return document.querySelector('product-gallery');
  }

  function getSwiper() {
    return getGalleryEl()?.swiper ?? null;
  }

  // Register a one-time slideChange listener that skips past bundle slides
  // when the user navigates there manually (swipe / arrows)
  function setupSkip(swiper) {
    if (swiper._tntSkipSetup) return;
    swiper._tntSkipSetup = true;

    swiper.on('slideChange', () => {
      if (swiper._tntBundleNav) return;
      const slide = swiper.slides[swiper.activeIndex];
      if (!slide?.querySelector('img[alt^="bundle:"]')) return;

      const step = swiper.activeIndex > swiper.previousIndex ? 1 : -1;
      let next = swiper.activeIndex + step;
      while (next >= 0 && next < swiper.slides.length) {
        if (!swiper.slides[next].querySelector('img[alt^="bundle:"]')) {
          swiper.slideTo(next);
          return;
        }
        next += step;
      }
    });
  }

  document.addEventListener('rebuy.ready', (event) => {
    if (event.detail.widget?.id !== '276830') return;
    const widget = event.detail.widget;

    const widgetEl = document.getElementById('rebuy-widget-276830');
    if (!widgetEl) return;

    customElements.whenDefined('product-gallery').then(() => {
      const swiper = getSwiper();
      if (swiper) setupSkip(swiper);
    });

    let lastSku = null;
    let preSelectionIndex = 0;

    function handleSelectionChange() {
      requestAnimationFrame(() => {
        const selected = (widget.data?.products ?? []).find((p) => p.selected);
        const sku = selected
          ? (selected.selected_variant ?? selected.variants?.[0])?.sku ?? null
          : null;

        if (sku === lastSku) return;

        const swiper = getSwiper();
        if (!swiper) return;
        setupSkip(swiper);

        if (sku && !lastSku) {
          preSelectionIndex = swiper.activeIndex;
        }

        lastSku = sku;

        if (sku) {
          const index = Array.from(swiper.slides).findIndex(
            (s) => s.querySelector(`img[alt="bundle:${sku}"]`)
          );
          if (index !== -1) {
            swiper._tntBundleNav = true;
            swiper.slideTo(index);
            swiper._tntBundleNav = false;
          }
        } else {
          swiper.slideTo(preSelectionIndex);
        }
      });
    }

    // 'change' catches checkbox toggles; 'click' catches image/title toggles.
    // rAF ensures Vue has updated product.selected before we read it.
    widgetEl.addEventListener('change', handleSelectionChange);
    widgetEl.addEventListener('click', handleSelectionChange);
  });
})();

/* ── Modular product hero — bundle wiring ─────────────────────────────────
   On product.modular pages, sections/custom-tnt-modular-hero.liquid renders
   an image-left / info-right block above the Rebuy bundle-builder widget.
   This handler:
     - Detects bundle-builder widgets (any id — multiple modular products
       are planned, each with its own widget).
     - Computes "From {lowest variant price}" on first ready and writes it
       into [data-modular-price-label].
     - Listens for rebuy.addProductToBundle / rebuy.removeProductFromBundle
       and re-renders the label as a 3-tier summary (RRP / Sale Price /
       Bundled Discount Price), hiding tiers that don't apply.
     - Builds a `combo:{qty}{key}-...` string from current quantities (key
       per piece comes from a `BundleKey:X` tag, falling back to first
       uppercase letter of the product title) and swaps the active image in
       [data-modular-gallery] to the first matching [alt^="combo:"]. No
       fallback if no match — the previously visible image stays.
   ──────────────────────────────────────────────────────────────────────── */

(function initModularBundle() {
  const PRICE_LABEL_SEL = '[data-modular-price-label]';
  const GALLERY_SEL = '[data-modular-gallery]';
  const IMAGE_SEL = '[data-modular-image]';
  const ECO_TAX_SEL = '[data-modular-eco-tax]';
  const log = (...args) => console.log('[modular-bundle]', ...args);

  // French eco-participation ("eco-part") — only shown on the FR market, to
  // mirror the normal product page (rendered there by a custom-liquid block
  // in the France context template). window.tntCountry is set in
  // layout/theme.liquid.
  const isFrenchMarket = window.tntCountry === 'FR';

  // The Liquid-rendered "From £X" innerHTML, snapshotted on first refresh so
  // we can restore it whenever the bundle returns to 0 selected items.
  let initialPriceLabelHtml = null;

  // Clearpay payment widget sync — snapshot the wrapper-product baseline on
  // first refresh, override with the bundle's discounted subtotal while
  // pieces are selected, restore baseline when the bundle is empty.
  //   <square-placement data-amount> in major units decimal string.
  // The Map entry tracks {base, expected} — `expected` is the value we last
  // wrote, so a MutationObserver can distinguish our writes from external
  // ones (Shopify Markets locale switches, theme variant handlers) and treat
  // those as a new baseline.
  //
  // Klarna OSM is intentionally NOT synced. Its app block re-asserts the
  // wrapper product price on every detected attribute change, so any value
  // we write is reverted within a frame. Instead we set a `data-bundle-active`
  // attribute on the hero and hide klarna-placement via CSS while the bundle
  // has items, since showing the wrapper price during bundle-building would
  // be misleading.
  const clearpayPlacements = new Map();
  let lastPaymentResync = null;
  // Tracks whether we've ever written a non-baseline amount. If we haven't,
  // there's no need to write the baseline back at totalQty=0 — the element
  // already has the Liquid-SSR'd baseline.
  let hasOverriddenPaymentWidgets = false;

  // Bundle data lives at widget.data.config (with widget.data.settings as a
  // duplicate). The bundle steps array — including the available products
  // and their variants — lives under that. Top-level widget.config is
  // undefined and widget.data.steps doesn't exist; both were the cause of
  // earlier blank-label failures.
  function getBundleConfig(widget) {
    return widget?.data?.config || widget?.data?.settings || null;
  }

  function getBundleSteps(widget) {
    return getBundleConfig(widget)?.steps || [];
  }

  function isBundleBuilder(widget) {
    if (!widget) return false;
    const cfg = getBundleConfig(widget);
    if (cfg?.type === 'bundle_builder') return true;
    return Boolean(cfg?.steps?.[0]?.products);
  }

  function getProductTags(product) {
    const raw = product?.tags ?? '';
    return Array.isArray(raw) ? raw : raw.split(',').map((t) => t.trim());
  }

  // Read the value of a `Bundle Piece: X` tag, e.g. "Corner Seat" / "Seat" /
  // "Footstool". Returns null if no such tag is set. The prefix is matched
  // case-insensitively: the bundle builder's Rebuy data (fetched with
  // locale=en) can hold every tag lowercased ("bundle piece: seat") — see
  // syncBundleAddons. shortCode uppercases the value, so its case is moot.
  const PIECE_TAG_PREFIX = 'bundle piece:';
  function bundlePieceTagValue(product) {
    const tagged = getProductTags(product).find((t) => t.toLowerCase().startsWith(PIECE_TAG_PREFIX));
    if (!tagged) return null;
    const value = tagged.slice(PIECE_TAG_PREFIX.length).trim();
    return value || null;
  }

  // Short code used in the combo:KEY image alt match. First character of the
  // Bundle Piece tag value uppercased ("Corner Seat" → C). Falls back to the
  // first uppercase letter of the product title if the tag isn't set.
  function shortCode(product) {
    const value = bundlePieceTagValue(product);
    if (value) return value[0].toUpperCase();
    const title = product?.title || '';
    const upper = title.match(/[A-Z]/);
    if (upper) return upper[0];
    return (title[0] || '?').toUpperCase();
  }

  // Bundle-builder methods (formatMoney, getBundleVariantQuantity,
  // bundleVariantPrice, getBundleSubtotal, etc.) live on the Vue instance
  // — exposed at widget.View — not on the widget controller itself. The
  // footstool widget happens to expose its methods directly on widget,
  // which is why the existing snippet works without this indirection.
  function vm(widget) {
    return widget?.View || widget;
  }

  function getQty(widget, variantId, stepIndex) {
    if (!variantId) return 0;
    try {
      return Number(vm(widget).getBundleVariantQuantity?.(variantId, stepIndex)) || 0;
    } catch {
      return 0;
    }
  }

  function variantIdOf(product) {
    return product?.selected_variant?.id ?? product?.selected_variant_id ?? product?.variants?.[0]?.id ?? null;
  }

  function fmt(widget, amount) {
    const v = vm(widget);
    if (typeof v.formatMoney === 'function') {
      try { return v.formatMoney(amount); } catch { /* fall through */ }
    }
    return String(amount);
  }

  function totalQuantity(widget) {
    let total = 0;
    getBundleSteps(widget).forEach((step, si) => {
      (step?.products || []).forEach((p) => {
        if (p?.type === 'placeholder') return;
        total += getQty(widget, variantIdOf(p), si);
      });
    });
    return total;
  }

  // Build "combo:C2-S3-F1" from the current per-piece quantities, in the
  // order Rebuy returns the products (Rebuy admin owns that order).
  // Format is short-code first, then qty — matches the merchant convention
  // for naming uploaded combo images.
  // Two forms are tried — pieces whose qty is 0 may or may not be encoded:
  //   full    → every piece including 0s, e.g. "combo:C0-S3-F0"
  //   compact → 0-qty pieces dropped,    e.g. "combo:S3"
  // The first matching alt wins. If no image matches, the caller leaves
  // the previously visible image in place (per spec).
  function buildComboKeys(widget) {
    const fullParts = [];
    const compactParts = [];
    getBundleSteps(widget).forEach((step, si) => {
      (step?.products || []).forEach((p) => {
        if (p?.type === 'placeholder') return;
        const code = shortCode(p);
        const q = getQty(widget, variantIdOf(p), si);
        fullParts.push(`${code}${q}`);
        if (q > 0) compactParts.push(`${code}${q}`);
      });
    });
    return {
      full: fullParts.length ? `combo:${fullParts.join('-')}` : null,
      compact: compactParts.length ? `combo:${compactParts.join('-')}` : null,
    };
  }

  function findImageByAlt(gallery, key) {
    if (!key) return null;
    const target = key.toLowerCase();
    const all = gallery.querySelectorAll(IMAGE_SEL);
    for (const img of all) {
      if ((img.getAttribute('alt') || '').toLowerCase() === target) return img;
    }
    return null;
  }

  function setActiveImage(gallery, target) {
    if (!target) return;
    const current = gallery.querySelector(`${IMAGE_SEL}.is-active`);
    if (current === target) return;
    if (current) {
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
    }
    target.classList.add('is-active');
    target.setAttribute('aria-hidden', 'false');
  }

  // First image in the gallery whose alt is NOT a combo: tag — i.e. the
  // default/hero image the merchant uploaded for an empty bundle state.
  function findDefaultImage(gallery) {
    const all = gallery.querySelectorAll(IMAGE_SEL);
    for (const img of all) {
      const alt = (img.getAttribute('alt') || '').toLowerCase();
      if (!alt.startsWith('combo:')) return img;
    }
    return null;
  }

  function updateGallery(widget) {
    const gallery = document.querySelector(GALLERY_SEL);
    if (!gallery) return;
    // When the bundle is empty, snap back to the default image — even if a
    // previous combo: match left a different one visible.
    if (totalQuantity(widget) === 0) {
      const defaultImg = findDefaultImage(gallery);
      if (defaultImg) setActiveImage(gallery, defaultImg);
      return;
    }
    const keys = buildComboKeys(widget);
    const img = findImageByAlt(gallery, keys.full) || findImageByAlt(gallery, keys.compact);
    if (img) setActiveImage(gallery, img);
  }

  // Sum of compare_at_price * qty across selected pieces. Falls back to the
  // current price when a piece has no compare_at — that piece simply doesn't
  // contribute to the "savings" gap, which is the correct behaviour.
  function compareAtSubtotal(widget) {
    let total = 0;
    const view = vm(widget);
    getBundleSteps(widget).forEach((step, si) => {
      (step?.products || []).forEach((p) => {
        if (p?.type === 'placeholder') return;
        (p.variants || []).forEach((v) => {
          const q = getQty(widget, v?.id, si);
          if (!q) return;
          let cmp;
          try { cmp = view.bundleVariantCompareAtPrice?.(p, v); } catch { /* fall through */ }
          if (typeof cmp !== 'number' || !cmp) {
            try { cmp = view.bundleVariantPrice?.(p, v); } catch { cmp = 0; }
          }
          if (typeof cmp !== 'number' || !cmp) {
            cmp = parseFloat(v?.compare_at_price ?? v?.price ?? 0);
          }
          total += (Number.isFinite(cmp) ? cmp : 0) * q;
        });
      });
    });
    return total;
  }

  // ── Eco-participation (French market only) ──────────────────────────────
  // Reads the variant-level `french.eco_tax` metafield off a Rebuy bundle
  // piece and returns it as a Number in euros (major units, e.g. 2.05), or 0.
  //
  // Rebuy only includes metafields when the widget/data source has them
  // enabled, and its serialisation shape isn't guaranteed, so we probe the
  // shapes we've seen: an object keyed by namespace → key (possibly wrapping
  // the value in a { value } object), or a flat array of
  // { namespace, key, value } records. Checks the variant first (the source
  // metafield is variant-level) then the product as a fallback.
  function metafieldValue(container, namespace, key) {
    if (!container) return null;
    const mf = container.metafields;
    if (!mf) return null;

    // Object shape: metafields.french.eco_tax (value may be scalar or {value})
    if (!Array.isArray(mf)) {
      const ns = mf[namespace];
      if (ns && typeof ns === 'object' && key in ns) {
        const entry = ns[key];
        if (entry && typeof entry === 'object' && 'value' in entry) return entry.value;
        return entry;
      }
      return null;
    }

    // Array shape: [{ namespace, key, value }, ...]
    const rec = mf.find((m) => m && m.namespace === namespace && m.key === key);
    return rec ? rec.value : null;
  }

  function pieceEcoTax(product) {
    const raw =
      metafieldValue(product?.selected_variant, 'french', 'eco_tax') ??
      metafieldValue(product, 'french', 'eco_tax');
    if (raw == null || raw === '') return 0;
    // Metafield is a decimal string using a dot ("2.05"); tolerate commas too.
    const n = parseFloat(String(raw).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }

  // Sum of eco_tax × qty across all selected pieces, in euros.
  function ecoTaxSubtotal(widget) {
    let total = 0;
    getBundleSteps(widget).forEach((step, si) => {
      (step?.products || []).forEach((p) => {
        if (p?.type === 'placeholder') return;
        const q = getQty(widget, variantIdOf(p), si);
        if (!q) return;
        total += pieceEcoTax(p) * q;
      });
    });
    return total;
  }

  // Format a euro amount the French way: comma decimal, 2 dp, trimmed to
  // whole euros when there are no cents (matches the normal page, which
  // renders the raw metafield e.g. "2,05").
  function formatEuroFr(amount) {
    const cents = Math.round(amount * 100);
    if (cents % 100 === 0) return String(cents / 100);
    return (cents / 100).toFixed(2).replace('.', ',');
  }

  // Render the combined eco-part line into [data-modular-eco-tax]. Hidden
  // when the bundle is empty or the combined value is 0. FR market only —
  // the container is only rendered by Liquid on FR, but we guard anyway.
  function updateEcoTax(widget) {
    if (!isFrenchMarket) return;
    const el = document.querySelector(ECO_TAX_SEL);
    if (!el) return;

    const total = ecoTaxSubtotal(widget);
    if (totalQuantity(widget) === 0 || total <= 0) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }

    el.hidden = false;
    el.innerHTML =
      "Inclus TVA et <span class=\"value\">" +
      formatEuroFr(total) +
      " €</span> d'eco-part";
  }

  function priceRow(modifier, label, amount, moneyClass) {
    return (
      `<div class="modular-hero__price-row modular-hero__price-row--${modifier}">` +
        `<span class="modular-hero__price-label">${label}</span>` +
        `<span class="modular-hero__price-amount ${moneyClass}">${amount}</span>` +
      `</div>`
    );
  }

  function renderLabel(widget) {
    const label = document.querySelector(PRICE_LABEL_SEL);
    if (!label) return;

    // Snapshot the Liquid-rendered "From £X" once so we can put it back
    // when bundle quantities return to 0.
    if (initialPriceLabelHtml === null) {
      initialPriceLabelHtml = label.innerHTML;
    }

    const totalQty = totalQuantity(widget);

    if (totalQty === 0) {
      label.innerHTML = initialPriceLabelHtml;
      return;
    }

    // Selected-products array + totals methods all live on the Vue instance.
    const view = vm(widget);
    const products =
      view.products ?? widget.data?.products ?? view.bundleProducts ?? [];

    let saleSubtotal = 0;
    try { saleSubtotal = Number(view.getBundleSubtotal?.(products)) || 0; } catch { saleSubtotal = 0; }

    let discounted = saleSubtotal;
    try {
      const d = Number(view.getBundleDiscountedSubtotal?.(products));
      if (Number.isFinite(d)) discounted = d;
    } catch { /* keep saleSubtotal */ }

    let hasBundleDiscount = false;
    try { hasBundleDiscount = !!view.bundleHasEligibleDiscount?.(); } catch { hasBundleDiscount = false; }
    if (!(discounted < saleSubtotal)) hasBundleDiscount = false;

    const rrp = compareAtSubtotal(widget);
    const hasCompareSavings = rrp > saleSubtotal;

    log('renderLabel selected branch', {
      saleSubtotal,
      discounted,
      rrp,
      hasBundleDiscount,
      hasCompareSavings,
    });

    // Localised label lookup. window.TNT_PRICE_LABELS is set by
    // snippets/custom.tnt.rebuy-bundle.liquid; missing keys fall through to
    // the English source.
    const trans = (k) => (window.TNT_PRICE_LABELS || {})[k] || k;

    const rows = [];
    if (hasCompareSavings) {
      rows.push(priceRow('rrp', trans('RRP'), fmt(widget, rrp), 'rebuy-money compare-at'));
    }
    if (hasBundleDiscount) {
      rows.push(priceRow('sale', trans('Sale Price'), fmt(widget, saleSubtotal), 'rebuy-money compare-at'));
      rows.push(priceRow('final', trans('Bundled Discount Price'), fmt(widget, discounted), 'rebuy-money sale'));
    } else {
      rows.push(priceRow('final', trans('Total'), fmt(widget, saleSubtotal), 'rebuy-money'));
    }
    label.innerHTML = rows.join('');
  }

  // Rate-limit MO-triggered resyncs. If an external script is repeatedly
  // asserting the wrapper amount (e.g. SA Variants' variant-change events
  // causing Klarna OSM to re-read product.price), our resync would fight
  // it at frame rate forever. After 4 reassertions inside a 1s window we
  // back off — Klarna will display the wrong amount, but the loop stops
  // and the user sees a stable (if incorrect) widget instead of flicker.
  const RESYNC_BURST_LIMIT = 4;
  const RESYNC_WINDOW_MS = 1000;

  function rateLimitedResync(info) {
    const now = Date.now();
    if (now - (info._resyncWindowStart || 0) > RESYNC_WINDOW_MS) {
      info._resyncWindowStart = now;
      info._resyncCount = 0;
    }
    info._resyncCount = (info._resyncCount || 0) + 1;
    if (info._resyncCount > RESYNC_BURST_LIMIT) {
      if (!info._rateLimitLogged) {
        log('payment widget resync rate-limited — external code keeps reverting our value');
        info._rateLimitLogged = true;
      }
      return;
    }
    lastPaymentResync?.();
  }

  function snapshotPaymentWidgets() {
    document.querySelectorAll('square-placement[data-amount]').forEach((el) => {
      if (clearpayPlacements.has(el)) return;
      const baseStr = el.getAttribute('data-amount');
      const base = parseFloat(baseStr);
      if (Number.isNaN(base)) return;
      const info = { base, expected: baseStr };
      clearpayPlacements.set(el, info);
      new MutationObserver(() => {
        const currentStr = el.getAttribute('data-amount');
        if (!currentStr || currentStr === info.expected) return;
        const current = parseFloat(currentStr);
        if (Number.isNaN(current)) return;
        info.base = current;
        rateLimitedResync(info);
      }).observe(el, { attributes: true, attributeFilter: ['data-amount'] });
    });
  }

  function syncPaymentWidgets(widget, totalQty) {
    snapshotPaymentWidgets();

    // Bundle subtotal in minor units (pence/cents). Same path the totals
    // branch of renderLabel uses — getBundleDiscountedSubtotal includes any
    // active tier discount; falling back to undiscounted subtotal if no tier
    // is reached.
    let bundleMinor = null;
    if (totalQty > 0) {
      const view = vm(widget);
      const products = view.products ?? widget.data?.products ?? view.bundleProducts ?? [];
      let amount = 0;
      try { amount = Number(view.getBundleDiscountedSubtotal?.(products)) || 0; } catch {}
      if (!amount) {
        try { amount = Number(view.getBundleSubtotal?.(products)) || 0; } catch {}
      }
      if (amount > 0) bundleMinor = amount;
    }

    // Toggle a flag on the hero so CSS can hide klarna-placement while the
    // bundle has items selected. We can't update Klarna's amount because
    // the Klarna OSM app block re-asserts the wrapper product's price on
    // every detected attribute change — see commit notes. Hiding it avoids
    // showing misinformation (£99.99 wrapper price while the customer is
    // building a £400 bundle). When the bundle is empty the wrapper price
    // happens to equal the cheapest piece, so it's still informative.
    const hero = document.querySelector('[data-modular-hero]');
    if (hero) hero.toggleAttribute('data-bundle-active', totalQty > 0);

    // Clearpay does respect attribute writes, so sync it. Skip the write
    // while baseline-only and we haven't overridden yet (the SSR value is
    // already correct, no point firing every observer in the page).
    if (bundleMinor == null && !hasOverriddenPaymentWidgets) {
      return;
    }

    if (bundleMinor != null) hasOverriddenPaymentWidgets = true;

    clearpayPlacements.forEach((info, el) => {
      const target = bundleMinor != null
        ? (bundleMinor / 100).toFixed(2)
        : info.base.toFixed(2);
      info.expected = target;
      el.setAttribute('data-amount', target);
    });
  }

  function setupWidget(widget) {
    if (!isBundleBuilder(widget)) return;
    if (widget._tntModularSetup) return;
    widget._tntModularSetup = true;

    // Coalesce overlapping refresh sources (Vue reactive watches, Rebuy events,
    // payment-widget MO resyncs) into a single write per animation frame. Two
    // synchronous calls to refresh() schedule one rAF callback; subsequent
    // calls within the same frame are no-ops. Without this, deep-reactive
    // watchers fire many times during state changes and we end up in a tight
    // attribute fight with Klarna's component (which re-asserts the wrapper
    // variant price on every detected attribute mutation).
    let scheduled = false;
    const refresh = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const totalQty = totalQuantity(widget);
        renderLabel(widget);
        updateGallery(widget);
        updateEcoTax(widget);
        syncPaymentWidgets(widget, totalQty);
      });
    };

    // The MutationObservers in snapshotPaymentWidgets call this when an
    // external write reverts our value, so they can re-apply the bundle
    // override on top of the new baseline.
    lastPaymentResync = refresh;

    refresh();

    // Rebuy persists the user's bundle selection in localStorage and rehydrates
    // it after rebuy.ready WITHOUT firing rebuy.addProductToBundle events. A
    // function-based Vue $watch only re-evaluates when totalQuantity actually
    // changes, so we don't fire on every irrelevant reactive mutation deep in
    // the products array.
    const view = vm(widget);
    if (typeof view?.$watch === 'function') {
      try {
        view.$watch(() => totalQuantity(widget), () => refresh());
      } catch (e) { /* widget Vue instance doesn't support $watch */ }

      // Rebuy auto-flips has_container_expanded to true when the bundle hits
      // its container_holder.max_value cap (6 items here). The original
      // bundle-builder template uses that flag to render a full-screen backdrop
      // and lock body scroll. We removed the expanded UI in our custom
      // template, so the locked scroll has no corresponding visible state and
      // appears as a "frozen page" to the user. Force the flag back to false
      // whenever Rebuy sets it.
      try {
        view.$watch('has_container_expanded', (val) => {
          if (val) view.has_container_expanded = false;
        });
      } catch (e) { /* property not on this Vue instance — nothing to do */ }
    }

    document.addEventListener('rebuy.addProductToBundle', (event) => {
      if (event.detail?.widget === widget) refresh();
    });
    document.addEventListener('rebuy.removeProductFromBundle', (event) => {
      if (event.detail?.widget === widget) refresh();
    });
  }

  document.addEventListener('rebuy.ready', (event) => {
    setupWidget(event.detail?.widget);
  });

  // Thumbnail carousel — click any [data-modular-thumb] to swap the main
  // gallery image to the matching [data-modular-image] (matched by image
  // id from Shopify). Bundle-driven combo: swaps still take precedence on
  // the next bundle change, which is the intended UX.
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-modular-thumb]');
    if (!thumb) return;
    const id = thumb.getAttribute('data-image-id');
    if (!id) return;
    const gallery = document.querySelector(GALLERY_SEL);
    if (!gallery) return;
    const target = gallery.querySelector(`[data-modular-image][data-image-id="${id}"]`);
    if (!target) return;
    setActiveImage(gallery, target);
    document.querySelectorAll('[data-modular-thumb]').forEach((t) => {
      t.classList.toggle('is-active', t === thumb);
    });
  });
})();